import React, { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CrmToaster, crmToast } from './crmUi'
import { useCrm } from './useCrm'
import { apiGet, apiPost, apiFetch } from '../../utils/api'
import { OB, DropMenu, useObFont } from './obDesign'

/**
 * COMMUNICATION & FOLLOW-UPS — rebuilt to Natalie's Followups.dc.html mockup
 * (design rebuild 3/3, 30 Oct 2026). Every customer email we draft, reviewed
 * and sent by a person; nothing sends itself. Left: the draft list (selected
 * = pale blue + inset bar). Right: the reader with Send from / To / Cc /
 * Subject, the grey body panel, attachments (Required blocks Send),
 * Suggested tasks Keep / Merge / Drop, and Dismiss · Hold · Edit · Send.
 */

interface Draft {
  id: string; onboardingId: string | null; leadId: string | null; company: string
  type: string; template: string | null; why: string | null; status: string
  ownerId: string | null; sendFrom: string | null; to: string | null; cc: string | null
  subject: string; body: string
  attachments: Array<{ name: string; url: string; required?: boolean }>
  suggested: Array<{ text: string; owner: string | null; needsOwner?: boolean; decision?: 'keep' | 'merge' | 'drop'; mergeInto?: string }>
  holdUntil: string | null; sentAt: string | null; sentBy: string | null; createdAt: string | null
}

const TYPES: Record<string, [string, string]> = {
  'Follow-up': ['#E4EDFC', '#1D4ED8'], 'Chase & check-in': ['#FDEBC8', '#7A4600'],
  'Welcome': ['#DDF3E4', '#146C3A'], 'Map ready': ['#E4EDFC', '#1D4ED8'],
  'Payment reminder': ['#FDE3E1', '#B42318'], 'Graduated': ['#DDF3E4', '#146C3A'],
  'Graduation check-in': ['#E3F4EA', '#146C3A'], 'Renewal': ['#F1F4F8', '#3A4555'],
}
const VIEW_OF: Record<string, string> = {
  'Follow-up': 'Follow-ups', 'Chase & check-in': 'Follow-ups',
  'Welcome': 'New customer emails', 'Map ready': 'New customer emails',
  'Payment reminder': 'Account emails', 'Graduated': 'Account emails',
  'Graduation check-in': 'Account emails', 'Renewal': 'Account emails',
}
const STATUS_LABEL: Record<string, string> = { to_send: 'Needs sign-off', coming_up: 'Coming up', held: 'Held', sent: 'Sent', dismissed: 'Dismissed' }
const statusPill = (s: string): React.CSSProperties => ({
  fontSize: 11.5, fontWeight: 800, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap',
  ...(s === 'sent' ? { background: OB.greenBg, color: OB.green } : s === 'coming_up' ? { background: OB.track, color: OB.muted }
    : s === 'held' ? { background: '#E4EDFC', color: OB.accent } : s === 'dismissed' ? { background: OB.track, color: OB.faint }
    : { background: OB.amberPale, color: OB.amberDark }),
})
const typeBadge = (t: string): React.CSSProperties => {
  const c = TYPES[t] || TYPES.Renewal
  return { fontSize: 11, fontWeight: 800, borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap', background: c[0], color: c[1] }
}

export const CrmObFollowups: React.FC = () => {
  useObFont()
  const navigate = useNavigate()
  const [sp] = useSearchParams()
  const [status, setStatus] = useState('to_send')
  const [typeF, setTypeF] = useState<string[]>([])
  const [viewTab, setViewTab] = useState('All')
  const [sel, setSel] = useState<string | null>(sp.get('draft'))
  const [editing, setEditing] = useState(false)
  const [body, setBody] = useState('')
  const [subject, setSubject] = useState('')

  const { data, reload } = useCrm<{ drafts: Draft[] }>(
    () => apiGet<{ drafts: Draft[] }>(`/crm/ob-drafts?status=${status === 'all' ? 'all' : status}`), { drafts: [] }, [status])
  const drafts = data.drafts

  const inView = (d: Draft, v: string) => v === 'All' || VIEW_OF[d.type] === v
  const filtered = useMemo(() => drafts.filter((d) => inView(d, viewTab) && (!typeF.length || typeF.includes(d.type))), [drafts, typeF, viewTab])
  const current = filtered.find((d) => d.id === sel) || filtered[0] || null

  const patch = (id: string, p: any, msg?: string) =>
    apiFetch(`/crm/ob-drafts/${id}`, { method: 'PATCH', body: JSON.stringify(p) }).then(() => { if (msg) crmToast(msg, 'success'); setEditing(false); reload() }).catch((e: any) => crmToast(e?.message || 'Not saved', 'error'))

  const requiredMissing = current ? current.attachments.filter((a) => a.required && !a.url) : []
  const keptCount = current ? current.suggested.filter((s) => s.decision === 'keep').length : 0
  const hasSugg = !!current && current.suggested.length > 0

  const send = () => {
    if (!current || requiredMissing.length) return
    if (!window.confirm(`Send "${current.subject}" to ${current.to || '—'}?`)) return
    apiPost(`/crm/ob-drafts/${current.id}/send`, {})
      .then((r: any) => { crmToast(`Sent ✓${r.tasksCreated ? ` + ${r.tasksCreated} task(s) added` : ''}`, 'success'); reload() })
      .catch((e: any) => crmToast(e?.message || 'Send failed', 'error'))
  }
  const setDecision = (i: number, decision: 'keep' | 'merge' | 'drop') => {
    if (!current) return
    patch(current.id, { suggested: current.suggested.map((s, idx) => (idx === i ? { ...s, decision } : s)) })
  }

  const outlineBtn: React.CSSProperties = { font: 'inherit', fontSize: 13, fontWeight: 700, background: '#fff', color: OB.ink, border: `1px solid ${OB.inputBorder}`, borderRadius: 10, padding: '8px 14px', cursor: 'pointer' }

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: OB.pageBg, fontFamily: OB.font, color: OB.ink }}>
      <CrmToaster />
      <div style={{ flex: 'none', padding: '16px 28px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <header style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'baseline', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Communication &amp; follow-ups</h1>
              <span style={{ fontSize: 12.5, color: OB.muted }}>Every customer email we draft, in one list. Nothing sends itself: whoever owns it reads and sends.</span>
            </div>
            <span style={{ fontSize: 12.5, color: OB.muted }}>{drafts.filter((d) => d.status === 'to_send').length} to send</span>
          </header>

          {/* view tabs */}
          <div role="tablist" style={{ display: 'flex', gap: 2, borderBottom: `1px solid ${OB.border}`, flexWrap: 'wrap' }}>
            {['All', 'Follow-ups', 'New customer emails', 'Account emails'].map((v) => {
              const on = viewTab === v
              const n = drafts.filter((d) => inView(d, v) && d.status === 'to_send').length
              return (
                <button key={v} role="tab" onClick={() => { setViewTab(v); setTypeF([]) }}
                  style={{ font: 'inherit', fontSize: 14, background: 'none', border: 0, padding: '10px 14px', minHeight: 44, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, ...(on ? { fontWeight: 800, color: OB.accent, borderBottom: `3px solid ${OB.accent}` } : { color: OB.muted, borderBottom: '3px solid transparent' }) }}>
                  {v} <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 999, padding: '0 6px', background: on ? OB.accentSoft : OB.track, color: on ? OB.accent : OB.muted }}>{n}</span>
                </button>
              )
            })}
          </div>

          {/* filters */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <DropMenu name="Status" value={{ to_send: 'To send', coming_up: 'Coming up', held: 'Held', sent: 'Sent', all: 'All' }[status] || status} active={status !== 'to_send'}
              options={([['to_send', 'To send'], ['coming_up', 'Coming up'], ['held', 'Held'], ['sent', 'Sent'], ['all', 'All']] as const).map(([k, l]) => ({ label: l, on: status === k, pick: () => { setStatus(k); setSel(null) } }))} />
            <DropMenu name="Type" value={typeF.length === 0 ? 'All types' : typeF.length === 1 ? typeF[0] : `${typeF.length} selected`} active={typeF.length > 0} multi onClear={() => setTypeF([])}
              options={Object.keys(TYPES).filter((k) => viewTab === 'All' || VIEW_OF[k] === viewTab).map((k) => ({ label: k, n: drafts.filter((d) => inView(d, viewTab) && d.type === k).length, on: typeF.includes(k), pick: () => setTypeF(typeF.includes(k) ? typeF.filter((x) => x !== k) : [...typeF, k]) }))} />
            <span style={{ fontSize: 12.5, color: OB.muted, alignSelf: 'center', marginLeft: 'auto' }}>{filtered.length} email{filtered.length === 1 ? '' : 's'}</span>
          </div>

      </div>

      {/* panes — fixed chrome above, both panes scroll (Lee, 30 Oct) */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 28px 16px' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
            {/* left: draft list */}
            <section style={{ flex: '1 1 360px', maxWidth: 460, minWidth: 0, background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 14, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
              {filtered.map((d) => {
                const on = current?.id === d.id
                return (
                  <button key={d.id} className={on ? undefined : 'ob-row'} onClick={() => { setSel(d.id); setEditing(false) }}
                    style={{ font: 'inherit', width: '100%', textAlign: 'left', background: on ? OB.accentPale : '#fff', border: 0, borderBottom: `1px solid ${OB.rowDiv}`, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 16px', color: OB.ink, boxShadow: on ? `inset 3px 0 0 ${OB.accent}` : 'none' }}>
                    <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                      <strong style={{ fontSize: 14 }}>{d.company}</strong>
                      <span style={statusPill(d.status)}>{STATUS_LABEL[d.status] || d.status}</span>
                    </span>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={typeBadge(d.type)}>{d.type}</span>
                      <span style={{ fontSize: 13 }}>{d.subject}</span>
                    </span>
                    <span style={{ fontSize: 12, color: OB.muted }}>{d.why || ''}{d.createdAt ? `${d.why ? ' · ' : ''}drafted ${new Date(d.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}</span>
                  </button>
                )
              })}
              {!filtered.length && <div style={{ padding: '20px 16px', fontSize: 13.5, color: OB.muted }}>Nothing here. Clear a filter to see more.</div>}
            </section>

            {/* right: reader */}
            <section style={{ flex: '2 1 520px', minWidth: 0, background: '#fff', border: `1px solid ${OB.border}`, borderRadius: 14, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14, height: '100%', boxSizing: 'border-box', overflowY: 'auto' }}>
              {!current ? <span style={{ fontSize: 13, color: OB.muted }}>Select a draft.</span> : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                      <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 16 }}>{current.company} · {current.subject}</strong>
                        <span style={typeBadge(current.type)}>{current.type}</span>
                      </span>
                      <span style={{ fontSize: 12.5, color: OB.muted }}>{current.why || ''}{current.leadId && (
                        <> · <button onClick={() => navigate(current.onboardingId ? `/crm/onboardings/co/${current.onboardingId}` : `/crm/lead/${current.leadId}`)} style={{ font: 'inherit', fontSize: 12.5, fontWeight: 700, color: OB.accent, background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>open {current.company} →</button></>
                      )}</span>
                    </div>
                    <span style={statusPill(current.status)}>{STATUS_LABEL[current.status] || current.status}</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '80px minmax(0, 1fr)', gap: '8px 12px', fontSize: 13.5, alignItems: 'center' }}>
                    <span style={{ color: OB.muted }}>Send from</span>
                    <input defaultValue={current.sendFrom || ''} placeholder="owner mailbox, or wequote@wetech.cloud"
                      onBlur={(e) => e.target.value !== (current.sendFrom || '') && patch(current.id, { sendFrom: e.target.value || null })}
                      style={{ font: 'inherit', border: `1px solid ${OB.inputBorder}`, borderRadius: 8, padding: '6px 10px', minHeight: 36, boxSizing: 'border-box' }} />
                    <span style={{ color: OB.muted }}>To</span>
                    <input defaultValue={current.to || ''} onBlur={(e) => e.target.value !== (current.to || '') && patch(current.id, { to: e.target.value })}
                      style={{ font: 'inherit', border: `1px solid ${OB.inputBorder}`, borderRadius: 8, padding: '6px 10px', minHeight: 36, boxSizing: 'border-box' }} />
                    <span style={{ color: OB.muted }}>Cc</span><span>wequote@ and the account manager — added automatically at send</span>
                    <span style={{ color: OB.muted }}>Subject</span>
                    {editing
                      ? <input value={subject} onChange={(e) => setSubject(e.target.value)} style={{ font: 'inherit', fontWeight: 700, border: `1px solid ${OB.inputBorder}`, borderRadius: 8, padding: '6px 10px' }} />
                      : <span style={{ fontWeight: 700 }}>{current.subject}</span>}
                  </div>

                  {editing ? (
                    <>
                      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={14}
                        style={{ font: 'inherit', fontSize: 13.5, lineHeight: 1.55, border: `1px solid ${OB.inputBorder}`, borderRadius: 10, padding: '16px 18px', resize: 'vertical' }} />
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => setEditing(false)} style={outlineBtn}>Cancel</button>
                        <button onClick={() => patch(current.id, { subject, body }, 'Saved')} style={{ ...outlineBtn, background: OB.accent, color: '#fff', border: 0, fontWeight: 800 }}>Save</button>
                      </div>
                    </>
                  ) : (
                    <div style={{ background: OB.headerBg, borderRadius: 10, padding: '16px 18px', fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{current.body}</div>
                  )}

                  {current.attachments.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 800, color: OB.muted }}>Attachments</span>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {current.attachments.map((a, i) => (
                          <span key={i} style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 13, borderRadius: 8, padding: '6px 10px', ...(a.url ? { background: OB.track, border: `1px solid ${OB.border}` } : { background: OB.redPale, border: '1px dashed #E79A91' }) }}>
                            <span>{a.url ? '✓' : '○'}</span>
                            <span style={{ fontWeight: 700 }}>{a.name}</span>
                            {a.required && !a.url && <span style={{ fontSize: 11, fontWeight: 800, color: OB.red }}>Required</span>}
                            {a.url && <a href={a.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 800, textDecoration: 'none' }}>Open</a>}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasSugg && (
                    <div style={{ border: `1px solid ${OB.border}`, borderRadius: 12, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', padding: '12px 16px', background: OB.headerBg, borderBottom: `1px solid ${OB.rowDiv}` }}>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <strong style={{ fontSize: 14 }}>From the call — for reference</strong>
                          <span style={{ fontSize: 12, color: OB.muted }}>Action points picked out of the call notes. Tasks are no longer created from calls (Lee, 3 Nov 2026) — add anything that matters by hand on To-do.</span>
                        </span>
                      </div>
                      {current.suggested.map((s, i) => {
                        const dec = s.decision || 'keep'
                        return (
                          <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 12, alignItems: 'center', padding: '10px 16px', borderBottom: `1px solid ${OB.rowDiv}` }}>
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                              <span style={{ fontSize: 13.5, fontWeight: 700, ...(dec === 'drop' ? { color: OB.faint, textDecoration: 'line-through' } : dec === 'merge' ? { color: OB.muted } : { color: OB.ink }) }}>{s.text}</span>
                              <span style={{ fontSize: 12, color: s.needsOwner ? OB.red : OB.muted, fontWeight: s.needsOwner ? 800 : 400 }}>{s.owner || 'Needs owner'}</span>
                            </span>
                            <span style={{ display: 'flex', border: `1px solid ${OB.inputBorder}`, borderRadius: 8, overflow: 'hidden' }}>
                              {(['keep', 'merge', 'drop'] as const).map((d, di) => {
                                const on = dec === d
                                return (
                                  <button key={d} onClick={() => setDecision(i, d)} aria-pressed={on}
                                    style={{ font: 'inherit', fontSize: 12.5, border: 0, borderLeft: di ? `1px solid ${OB.border}` : 0, padding: '0 11px', minHeight: 32, cursor: 'pointer', textTransform: 'capitalize', ...(on ? { background: d === 'keep' ? '#146C3A' : d === 'merge' ? OB.accent : OB.muted, color: '#fff', fontWeight: 700 } : { background: '#fff', color: OB.ink2 }) }}>{d}</button>
                                )
                              })}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {current.status !== 'sent' && current.status !== 'dismissed' && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <button style={outlineBtn} onClick={() => { const r = window.prompt('Dismiss — why?'); if (r != null) patch(current.id, { status: 'dismissed', dismissReason: r }, 'Dismissed') }}>Dismiss</button>
                      <button style={outlineBtn} onClick={() => { const d = window.prompt('Hold until (YYYY-MM-DD, blank = indefinitely)'); if (d != null) patch(current.id, { status: 'held', holdUntil: d || null }, 'Held') }}>Hold</button>
                      <button style={outlineBtn} onClick={() => { setEditing(true); setBody(current.body); setSubject(current.subject) }}>Edit</button>
                      {requiredMissing.length > 0 && <span style={{ fontSize: 12.5, fontWeight: 700, color: OB.red }}>Attach the {requiredMissing.map((a) => a.name.toLowerCase()).join(', ')} to send</span>}
                      <button onClick={send} disabled={requiredMissing.length > 0}
                        style={{ font: 'inherit', fontSize: 13, fontWeight: 800, border: 0, borderRadius: 10, padding: '8px 20px', ...(requiredMissing.length ? { background: OB.inputBorder, color: '#fff', cursor: 'not-allowed' } : { background: OB.accent, color: '#fff', cursor: 'pointer' }) }}>
                        Send
                      </button>
                    </div>
                  )}
                  {current.status === 'sent' && <span style={{ fontSize: 12.5, color: OB.green, fontWeight: 700 }}>Sent {current.sentAt ? new Date(current.sentAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : ''} by {current.sentBy}</span>}
                </>
              )}
            </section>
          </div>
          <span style={{ flex: 'none', fontSize: 12, color: OB.muted, lineHeight: 1.5 }}>One list of every customer email we draft, with the type on each: follow-ups, chases and check-ins, welcome, map ready, payment reminders, graduated, graduation check-ins and renewal. Click one to read and send it. Nothing sends itself.</span>
      </div>
    </div>
  )
}

export default CrmObFollowups
